<template>
    <div>
      <strong>{{ tip_header }}: </strong>{{ tip }}
    </div>
  </template>
  
  <script setup>
  import { computed } from 'vue';
  import { defineProps } from 'vue';
  
  const props = defineProps({
    have_solar: { type: Number, required: true }, // 0 or 1
    have_battery: { type: Number, required: true }, // 0 or 1
    total_consumption: { type: Number, required: true }, // kWh
    produced_energy: { type: Number, required: true }, // kWh
    solar_consumed_percentage: { type: Number, required: true }, // %
    solar_fed_percentage: { type: Number, required: true }, // %
    grid_percentage: { type: Number, required: true }, // %
    solar_percentage: { type: Number, required: true }, // %
    battery_percentage: { type: Number, required: true } // %
  });
  
  const tips = computed(() => {
    const {
      have_solar,
      have_battery,
      total_consumption,
      produced_energy,
      solar_consumed_percentage,
      solar_fed_percentage,
      grid_percentage,
      solar_percentage,
      battery_percentage
    } = props;
  
    // Main conditions for home user
    if (!have_solar && grid_percentage > 80) {
      return {
        tip_header: "Consider Installing Solar",
        tip: `You rely heavily on the grid (${grid_percentage.toFixed(1)}% of consumption). Installing solar panels could significantly lower your bill.`
      };
    }
  
    if (have_solar && solar_fed_percentage > 30) {
      return {
        tip_header: "Excess Solar Energy Detected",
        tip: `You are feeding ${(solar_fed_percentage).toFixed(1)}% of your solar production back to the grid. Consider using more energy during the day or installing a battery to store excess energy.`
      };
    }
  
    if (have_solar && solar_consumed_percentage < 50) {
      return {
        tip_header: "Low Solar Usage",
        tip: `You are consuming only ${(solar_consumed_percentage).toFixed(1)}% of your solar production. Try running high-energy appliances during sunlight hours.`
      };
    }
  
    if (have_battery && battery_percentage < 10 && solar_fed_percentage > 20) {
      return {
        tip_header: "Battery Underused",
        tip: `Your battery contributes only ${(battery_percentage).toFixed(1)}% to your energy use, while ${(solar_fed_percentage).toFixed(1)}% of solar is fed to the grid. Adjust charging settings to store more energy.`
      };
    }
  
    if (total_consumption > 30 && grid_percentage > 70) {
      return {
        tip_header: "High Grid Consumption",
        tip: `Your total consumption is high (${total_consumption} kWh) and ${(grid_percentage).toFixed(1)}% comes from the grid. Consider energy efficiency upgrades or adding solar.`
      };
    }
  
    return {
      tip_header: "Energy Usage Balanced",
      tip: `Your energy mix looks good. Keep monitoring to further optimize self-consumption and reduce grid reliance.`
    };
  });
  
  const tip_header = computed(() => tips.value.tip_header);
  const tip = computed(() => tips.value.tip);
  </script>
  